import { useQuery, useQueryClient } from '@tanstack/react-query';

import React from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';
import { useAdminGhosting } from '@dashboard/context/AdminGhostingContext/AdminGhostingContext';

import { submissionKey } from '@dashboard/lib/queryKeys';

import { fetcher } from '@dashboard/services/request';
import getSubmissionStatusLabel from '@dashboard/services/status/getSubmissionStatusLabel';

// Import scene components using relative paths
import SubmissionsDetail from './scenes/SubmissionsDetail';
import SubmissionsDetailEmailLogs from './scenes/SubmissionsDetailEmailLogs';

// Import necessary UI components
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderNav from '@dashboard/components/CardHeaderNav';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import Spinner from '@dashboard/components/Spinner';
import StatusBadge from '@dashboard/components/StatusBadge';
import Dash from '@/dashboard/components/Dash';

// Define the tabs (pass only submissionId, submission)
const getTabs = ({ submissionId, submission }) => [
  {
    name: 'Details',
    href: `/submissions/${submissionId}`,
    render: props => <SubmissionsDetail {...props} submission={submission} />,
  },
  {
    name: 'Emails',
    href: `/submissions/${submissionId}/emails`,
    render: props => <SubmissionsDetailEmailLogs {...props} submission={submission} />,
  },
];

const SubmissionsDetailOverview = () => {
  const { token, sellerId } = useAuth();
  const { setAdminGhostingSellerId } = useAdminGhosting();
  const {
    params: { submissionId },
  } = useRouteMatch();
  const queryClient = useQueryClient();
  const { emitToast } = useToast();

  const {
    data: submission,
    isError,
    isLoading,
  } = useQuery({
    queryKey: submissionKey(submissionId),
    queryFn: () => fetcher(`/sellers/${sellerId}/submissions/${submissionId}`, token),
    enabled: !!submissionId,
  });

  React.useEffect(() => {
    if (submission?.seller) {
      setAdminGhostingSellerId(submission.seller);
    }
  }, [submission?.seller, setAdminGhostingSellerId]);

  if (isLoading) {
    return (
      <Card variant="root">
        <CardContent variant="root">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  if (!submission || isError) {
    return (
      <Card variant="root">
        <CardContent variant="root">Something went wrong</CardContent>
      </Card>
    );
  }

  // Pass only necessary props
  const tabs = getTabs({
    submissionId,
    submission,
  });

  return (
    <>
      <Card variant="root">
        <CardHeader variant="root">
          <div className="space-y-2 w-full">
            <CardHeaderTitle variant="root">{submission.page?.name || <Dash />}</CardHeaderTitle>
            <div className="flex flex-row gap-x-2 items-center">
              <StatusBadge
                status={submission.status}
                livemode={submission.livemode}
                label={getSubmissionStatusLabel(submission.status)}
              />
            </div>
          </div>
          <CardHeaderNav.Container tabs={tabs}>
            {({ currentTab }) =>
              tabs.map(tab => (
                <CardHeaderNav.Tab key={tab.name} tab={tab} current={currentTab.href === tab.href}>
                  {tab.name}
                </CardHeaderNav.Tab>
              ))
            }
          </CardHeaderNav.Container>
        </CardHeader>
        <CardContent variant="compact">
          <Switch>
            {tabs.map(tab => (
              <Route key={tab.href} path={tab.href} render={props => tab.render(props)} exact />
            ))}
          </Switch>
        </CardContent>
      </Card>
    </>
  );
};

export default SubmissionsDetailOverview;
